import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../../components/ui/alert-dialog';
import { cn } from '../../../lib/utils';

interface ConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  /** The dialog heading. A plain string is the common case; a node lets a caller
   * compose rich content (e.g. a bidi-isolated entity name via `<Trans>` +
   * `<BidiText>`) that a bare string can't carry. */
  title: ReactNode;
  /** The dialog body. A plain string is the common case; a node lets a caller
   * compose rich content (e.g. bidi-isolated entity names / tokens via
   * `<BidiText>` / `<LtrToken>`) that a bare string can't carry. */
  message: ReactNode;
  /** Defaults to the translated `common:app.confirm`. */
  confirmLabel?: string;
  /** Defaults to the translated `common:app.cancel`. */
  cancelLabel?: string;
  /**
   * The confirmed action is running.
   *
   * Blocks a second confirm and swaps the confirm label for `loadingLabel`, but
   * deliberately does NOT disable Cancel: Radix `AlertDialog` ignores outside
   * clicks by design, so with both buttons dead the only way out is the Escape
   * key — which a touch user does not have. A confirm whose mutation runs long
   * would then trap the user in a modal with nothing to press (WI 74ca952a).
   */
  isLoading?: boolean;
  /**
   * What the confirm button says while `isLoading`. Defaults to the translated
   * `common:app.processing`. Pass the specific verb where the action has one
   * ("Deleting…") — a spinner beside the unchanged idle label reads as a frozen
   * dialog, not as work in progress.
   */
  loadingLabel?: string;
  /** Renders the confirm button in the destructive (red) fill. Defaults to
   * `false` — red is reserved for genuinely destructive actions (delete, remove,
   * void, cancel an order, discard unsaved work), not for forward ones (approve,
   * confirm, submit, send, publish). Pass `destructive` at every site that
   * removes or reverses data. */
  destructive?: boolean;
  /**
   * Drop the Cancel button, leaving one acknowledging action.
   *
   * For the case where there is genuinely nothing to decline: the app has already
   * done (or refused to do) the thing, and the dialog exists to say so. Offering
   * "Cancel" beside "Got it" there invites the reviewer to look for the difference
   * between two buttons that do the same thing. NOT an escape hatch for a real
   * confirmation — every action a user could reasonably want to back out of keeps
   * its Cancel. Escape and the overlay still close the dialog either way.
   *
   * With no Cancel, the confirm button is the dialog's LAST control, so `isLoading`
   * does not disable it — zero live controls is the trap this component exists not
   * to spring. Pair the two only on an acknowledgement, which is the only thing
   * `hideCancel` is for: there is nothing to submit twice.
   */
  hideCancel?: boolean;
  /**
   * Block the confirm action without claiming anything is in flight.
   *
   * `isLoading` also disables it, but it renders a spinner and swaps in
   * `loadingLabel` — correct for a submit already running, wrong for a gate the
   * user has yet to satisfy (a typed confirmation, an unticked acknowledgement).
   * Those must show no spinner and keep the idle confirm label.
   */
  confirmDisabled?: boolean;
  children?: ReactNode;
}

export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel,
  cancelLabel,
  isLoading = false,
  loadingLabel,
  destructive = false,
  hideCancel = false,
  confirmDisabled = false,
  children,
}: ConfirmDialogProps) {
  const { t } = useTranslation('common');

  return (
    <AlertDialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{message}</AlertDialogDescription>
        </AlertDialogHeader>
        {children}
        <AlertDialogFooter>
          {!hideCancel && (
            <AlertDialogCancel>
              {/* While the action is in flight this says "Close", not "Cancel":
                  dismissing the dialog does not recall a request the server is
                  already serving, and a Cancel that cannot abort is a lie. */}
              {cancelLabel ?? (isLoading ? t('app.close') : t('app.cancel'))}
            </AlertDialogCancel>
          )}
          <AlertDialogAction
            onClick={onConfirm}
            disabled={(isLoading && !hideCancel) || confirmDisabled}
            data-testid="confirm-dialog-confirm"
            className={cn(
              destructive &&
                'bg-destructive text-destructive-foreground hover:bg-destructive/90'
            )}
          >
            {isLoading && <Loader2 className="me-2 h-4 w-4 animate-spin" />}
            {isLoading
              ? (loadingLabel ?? t('app.processing'))
              : (confirmLabel ?? t('app.confirm'))}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
